import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ReportsSubNav from "@/components/ReportsSubNav";
import { Coins, Infinity as InfinityIcon, AlertTriangle, Bot } from "lucide-react";

/**
 * v6.1 Phase 2d — Reports > AI Token Usage.
 *
 * Every user: their own monthly usage vs their own cap.
 * VC Admin / Senior VC additionally: tenant-wide progress + per-person and
 * per-agent breakdowns + blocked-attempt count.
 * Junior VC sees only their own figure.
 */
export default function AiTokenUsage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/v61/reports/ai-usage").then(({ data }) => setData(data));
  }, []);

  if (!data) return (
    <div className="p-8">
      <ReportsSubNav />
      <div className="cw-card p-10 text-center text-[var(--cw-muted)] text-sm mt-6">
        Loading…
      </div>
    </div>
  );

  const period = new Date(data.period_start).toLocaleDateString(undefined, {
    month: "long", year: "numeric",
  });

  return (
    <div className="p-8" data-testid="ai-usage-report">
      <ReportsSubNav />
      <div className="mt-6 space-y-5">
        <div>
          <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-1">
            <Coins size={12} /> AI Token Usage — {period}
          </div>
          <h1 className="cw-h1 mt-1">Your monthly AI usage</h1>
        </div>

        {/* My usage */}
        <div className="cw-card p-5">
          <div className="text-sm font-medium">Your usage this month</div>
          <ProgressBlock
            testid="my-usage"
            used={data.my.tokens}
            limit={data.my.limit_value}
            unlimited={data.my.is_unlimited}
            percent={data.my.percent}
          />
          <div className="text-xs text-[var(--cw-muted)] mt-1">
            {data.my.calls} LLM call{data.my.calls === 1 ? "" : "s"} succeeded this month
          </div>
        </div>

        {/* Tenant-wide */}
        {data.tenant && (
          <div className="cw-card p-5">
            <div className="text-sm font-medium">Workspace-wide usage</div>
            <ProgressBlock
              testid="tenant-usage"
              used={data.tenant.tokens}
              limit={data.tenant.limit_value}
              unlimited={data.tenant.is_unlimited}
              percent={data.tenant.percent}
            />
            <div className="text-xs text-[var(--cw-muted)] mt-1">
              {data.tenant.calls} LLM call{data.tenant.calls === 1 ? "" : "s"} across the workspace this month
              {data.blocked_count > 0 && (
                <span className="ml-3 text-[#B02B2B]">
                  <AlertTriangle size={11} className="inline" /> {data.blocked_count} blocked attempt{data.blocked_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Per-person */}
        {data.persons && (
          <div className="cw-card p-5">
            <h3 className="cw-h3">Per-person breakdown</h3>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {data.persons.length === 0 && (
                <div className="text-xs text-[var(--cw-muted)] italic">
                  No usage recorded yet this month.
                </div>
              )}
              {data.persons.map((p) => (
                <div key={p.user_id}
                  className="flex items-center gap-3 border rounded-md p-3 flex-wrap"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`ai-usage-person-${p.user_id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium">{p.full_name || p.email}</div>
                    <div className="text-[11px] text-[var(--cw-muted)]">{p.email} · {p.role}</div>
                  </div>
                  <div className="w-64">
                    <ProgressBlock
                      testid={`ai-usage-person-progress-${p.user_id}`}
                      used={p.tokens}
                      limit={p.limit_value}
                      unlimited={p.is_unlimited}
                      percent={p.percent}
                      compact
                    />
                    <div className="text-[11px] text-[var(--cw-muted)]">{p.calls} calls</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-agent */}
        {data.agents && (
          <div className="cw-card p-5">
            <h3 className="cw-h3">Per-agent breakdown</h3>
            <p className="text-xs text-[var(--cw-muted)] mt-1">
              For visibility only — no cap exists at the Agent level.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {data.agents.length === 0 && (
                <div className="text-xs text-[var(--cw-muted)] italic">
                  No Agent usage this month.
                </div>
              )}
              {data.agents.map((a) => (
                <div key={a.agent_id}
                  className="flex items-center gap-3 border rounded-md p-3 flex-wrap"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`ai-usage-agent-${a.agent_id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium inline-flex items-center gap-2">
                      <Bot size={12} /> {a.agent_name}
                    </div>
                    <div className="text-[11px] text-[var(--cw-muted)]">
                      {a.agent_type}
                    </div>
                  </div>
                  <div className="text-sm font-mono">
                    {a.tokens.toLocaleString()} tokens
                  </div>
                  <div className="text-[11px] text-[var(--cw-muted)]">{a.calls} calls</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProgressBlock({ used, limit, unlimited, percent, compact, testid }) {
  if (unlimited) {
    return (
      <div className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
        style={{ background: "rgba(40,42,116,0.08)", color: "var(--cw-primary)" }}
        data-testid={`${testid}-unlimited`}>
        <InfinityIcon size={12} /> No Limit — {used.toLocaleString()} tokens
      </div>
    );
  }
  const pct = Math.min(100, Math.max(0, percent || 0));
  const color = pct >= 100 ? "#B02B2B" : pct >= 80 ? "#E0A100" : "#2C9A52";
  return (
    <div className={compact ? "mt-1" : "mt-2"} data-testid={testid}>
      <div className="h-2 rounded overflow-hidden" style={{ background: "rgba(0,0,0,0.06)" }}>
        <div className="h-full transition"
          style={{ width: `${pct}%`, background: color }}
          data-testid={`${testid}-bar`} />
      </div>
      <div className={`${compact ? "text-[11px]" : "text-xs"} text-[var(--cw-muted)] mt-1`}>
        {used.toLocaleString()} / {(limit || 0).toLocaleString()} tokens
        {pct >= 80 && pct < 100 && <span className="ml-2 text-[#8A6510]">80%+</span>}
        {pct >= 100 && <span className="ml-2 text-[#B02B2B]">Blocked</span>}
      </div>
    </div>
  );
}
